import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    ArrowDownCircle, ArrowUpCircle, RefreshCw, X,
    TrendingDown, TrendingUp, AlertCircle, Calendar,
    ChevronRight, Loader2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Visual treatment per direction marker returned by the backend.
const DIR_STYLE = {
    in:         { ring: 'border-[#39FF14]/30 bg-[#39FF14]/5',   accent: 'text-[#39FF14]', icon: ArrowUpCircle,   sign: '+' },
    out:        { ring: 'border-[#FFD700]/30 bg-[#FFD700]/5',   accent: 'text-[#FFD700]', icon: ArrowDownCircle, sign: '-' },
    mixed:      { ring: 'border-[#A855F7]/30 bg-[#A855F7]/5',   accent: 'text-[#A855F7]', icon: TrendingDown,    sign: '±' },
    cash_out:   { ring: 'border-[#FF6B35]/30 bg-[#FF6B35]/5',   accent: 'text-[#FF6B35]', icon: ArrowDownCircle, sign: '-' },
    casino_out: { ring: 'border-[#3B82F6]/30 bg-[#3B82F6]/5',   accent: 'text-[#3B82F6]', icon: TrendingDown,    sign: '→' },
    casino_in:  { ring: 'border-[#10B981]/30 bg-[#10B981]/5',   accent: 'text-[#10B981]', icon: TrendingUp,      sign: '←' },
};

// Today's date in Central Time (yyyy-mm-dd).
const todayCT = () => {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date());
    const y = parts.find(p => p.type === 'year').value;
    const m = parts.find(p => p.type === 'month').value;
    const d = parts.find(p => p.type === 'day').value;
    return `${y}-${m}-${d}`;
};

const fmtCT = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        });
    } catch {
        return iso;
    }
};

const DrilldownModal = ({ open, onClose, date, bucketKey, bucketLabel }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !bucketKey) return;
        setLoading(true);
        axios
            .get(`${API}/admin/recon/drilldown`, { params: { date, bucket: bucketKey, limit: 500 } })
            .then(({ data }) => setRows(data.transactions || []))
            .catch(() => toast.error('Could not load drilldown'))
            .finally(() => setLoading(false));
    }, [open, bucketKey, date]);

    if (!open) return null;

    const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

    return (
        <div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="recon-drilldown-modal"
        >
            <div
                className="card-dark w-full max-w-3xl max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                    <div>
                        <p className="text-[10px] text-[#A0AAB2] uppercase tracking-widest">Drilldown · {date}</p>
                        <h3 className="text-white text-lg font-black" data-testid="recon-drilldown-title">{bucketLabel}</h3>
                        <p className="text-xs text-[#A0AAB2] mt-0.5">
                            {rows.length} transaction{rows.length === 1 ? '' : 's'} ·
                            <span className={`font-mono font-bold ml-1 ${total >= 0 ? 'text-[#39FF14]' : 'text-[#FF6B35]'}`}>
                                ${total.toFixed(2)}
                            </span>
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5" data-testid="recon-drilldown-close">
                        <X className="w-5 h-5 text-white" />
                    </button>
                </div>
                <div className="overflow-y-auto flex-1">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 text-[#FFD700] animate-spin" />
                        </div>
                    ) : rows.length === 0 ? (
                        <p className="text-center text-[#A0AAB2] py-12 text-sm">No transactions in this bucket today.</p>
                    ) : (
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-[#0a0a0a]">
                                <tr className="text-left text-[10px] text-[#A0AAB2] uppercase tracking-widest">
                                    <th className="px-3 py-2">When (CT)</th>
                                    <th className="px-3 py-2">Player</th>
                                    <th className="px-3 py-2">Type</th>
                                    <th className="px-3 py-2 text-right">$</th>
                                    <th className="px-3 py-2">Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`recon-row-${r.id}`}>
                                        <td className="px-3 py-2 text-[#A0AAB2] whitespace-nowrap">{fmtCT(r.created_at)}</td>
                                        <td className="px-3 py-2 text-white">
                                            <div>{r.user_name}</div>
                                            <div className="text-[10px] text-[#666]">{r.user_email}</div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="px-1.5 py-0.5 rounded bg-white/5 text-[#A0AAB2] text-[10px] font-mono">
                                                {r.transaction_type}
                                            </span>
                                            {r.is_comp && (
                                                <span className="ml-1 px-1.5 py-0.5 rounded bg-[#A855F7]/20 text-[#A855F7] text-[10px] font-black uppercase">COMP</span>
                                            )}
                                            {r.is_loan && (
                                                <span className="ml-1 px-1.5 py-0.5 rounded bg-[#FF6B35]/20 text-[#FF6B35] text-[10px] font-black uppercase">LOAN</span>
                                            )}
                                        </td>
                                        <td className={`px-3 py-2 text-right font-mono font-bold ${(r.amount || 0) >= 0 ? 'text-[#39FF14]' : 'text-[#FF6B35]'}`}>
                                            {(r.amount || 0).toFixed(2)}
                                        </td>
                                        <td className="px-3 py-2 text-[#A0AAB2] max-w-xs truncate" title={r.description}>{r.description}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

const Bucket = ({ b, onClick }) => {
    const style = DIR_STYLE[b.direction] || DIR_STYLE.out;
    const Icon = style.icon;
    const top = (b.top_consumers || []).slice(0, 3);
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={b.count === 0}
            className={`text-left rounded-xl border-2 ${style.ring} p-3 transition-all ${
                b.count === 0 ? 'opacity-40 cursor-default' : 'hover:bg-white/[.04] active:scale-[0.98]'
            }`}
            data-testid={`recon-bucket-${b.key}`}
        >
            <div className="flex items-start justify-between gap-2">
                <Icon className={`w-4 h-4 ${style.accent} flex-shrink-0 mt-0.5`} />
                {b.count > 0 && <ChevronRight className="w-4 h-4 text-[#666]" />}
            </div>
            <p className="text-[10px] text-[#A0AAB2] uppercase tracking-widest mt-2 leading-tight">{b.label}</p>
            <p className={`text-xl font-black font-mono mt-1 ${style.accent}`}>
                ${Math.abs(b.total).toFixed(2)}
            </p>
            <p className="text-[10px] text-[#666] mt-0.5">
                {b.count} tx{b.count === 1 ? '' : 's'}
            </p>
            {top.length > 0 && (
                <div className="mt-2 pt-2 border-t border-white/5 space-y-0.5">
                    {top.map((c) => (
                        <div key={c.user_id} className="text-[10px] flex justify-between gap-1">
                            <span className="text-[#A0AAB2] truncate">{c.name}</span>
                            <span className={`font-mono font-bold ${style.accent} flex-shrink-0`}>
                                ${Math.abs(c.total).toFixed(0)}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </button>
    );
};

const CreditReconciliationCard = () => {
    const [date, setDate] = useState(todayCT());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [drilldown, setDrilldown] = useState({ open: false, key: null, label: null });

    const fetchRecon = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/recon/daily`, { params: { date } });
            setData(data);
        } catch {
            if (!silent) toast.error('Failed to load reconciliation');
        } finally {
            if (!silent) setLoading(false);
        }
    }, [date]);

    useEffect(() => { fetchRecon(); }, [fetchRecon]);

    const buckets = data?.buckets || [];

    // Top-of-card summary numbers — gives a 3-second snapshot
    const totalOut = buckets
        .filter(b => ['out', 'mixed', 'cash_out'].includes(b.direction))
        .reduce((s, b) => s + Math.abs(b.total), 0);
    const totalIn = buckets
        .filter(b => b.direction === 'in')
        .reduce((s, b) => s + b.total, 0);
    const netCasino = (buckets.find(b => b.key === 'casino_redeems')?.total || 0)
                    + (buckets.find(b => b.key === 'casino_topups')?.total || 0);
    const promoSpend = buckets.find(b => b.key === 'free_play_bonuses')?.total || 0;
    const playbackSpend = buckets.find(b => b.key === 'playbacks_reversals')?.total || 0;

    return (
        <div className="card-dark p-4 sm:p-5" data-testid="credit-recon-card">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                    <h3 className="text-white text-base font-black flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-[#FFD700]" />
                        Credit Reconciliation
                    </h3>
                    <p className="text-[10px] text-[#A0AAB2] mt-0.5">
                        Click any tile to see every transaction · all times Central
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#A0AAB2]" />
                    <input
                        type="date"
                        value={date}
                        max={todayCT()}
                        onChange={(e) => setDate(e.target.value)}
                        className="bg-[#0a0a0a] border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
                        data-testid="recon-date-picker"
                    />
                    <button
                        onClick={() => fetchRecon()}
                        disabled={loading}
                        className="p-1.5 rounded-lg border border-white/10 hover:bg-white/5"
                        data-testid="recon-refresh-btn"
                    >
                        <RefreshCw className={`w-4 h-4 text-[#A0AAB2] ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Quick summary strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                <div className="rounded-lg bg-[#39FF14]/5 border border-[#39FF14]/20 px-3 py-2">
                    <p className="text-[9px] text-[#A0AAB2] uppercase tracking-widest">Credits Bought</p>
                    <p className="text-[#39FF14] font-mono font-black text-base">${totalIn.toFixed(0)}</p>
                </div>
                <div className="rounded-lg bg-[#FFD700]/5 border border-[#FFD700]/20 px-3 py-2">
                    <p className="text-[9px] text-[#A0AAB2] uppercase tracking-widest">Credits Disbursed</p>
                    <p className="text-[#FFD700] font-mono font-black text-base">${totalOut.toFixed(0)}</p>
                </div>
                <div className="rounded-lg bg-[#FF6B35]/5 border border-[#FF6B35]/20 px-3 py-2">
                    <p className="text-[9px] text-[#A0AAB2] uppercase tracking-widest">Promo + Playback</p>
                    <p className="text-[#FF6B35] font-mono font-black text-base">
                        ${(promoSpend + playbackSpend).toFixed(0)}
                    </p>
                </div>
                <div className="rounded-lg bg-[#3B82F6]/5 border border-[#3B82F6]/20 px-3 py-2">
                    <p className="text-[9px] text-[#A0AAB2] uppercase tracking-widest">Net Casino Flow</p>
                    <p className={`font-mono font-black text-base ${netCasino >= 0 ? 'text-[#39FF14]' : 'text-[#FF6B35]'}`}>
                        ${netCasino.toFixed(0)}
                    </p>
                </div>
            </div>

            {/* Buckets grid */}
            {loading && !data ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-[#FFD700] animate-spin" />
                </div>
            ) : (
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
                    {buckets.map((b) => (
                        <Bucket
                            key={b.key}
                            b={b}
                            onClick={() => setDrilldown({ open: true, key: b.key, label: b.label })}
                        />
                    ))}
                </div>
            )}

            <DrilldownModal
                open={drilldown.open}
                onClose={() => setDrilldown({ open: false, key: null, label: null })}
                date={date}
                bucketKey={drilldown.key}
                bucketLabel={drilldown.label}
            />
        </div>
    );
};

export default CreditReconciliationCard;
